import {
    actionableGroups,
    groupsJoinedRecently,
    otherGroupsUserOwns,
    allOtherGroups,
} from './unifiedGroupListSelectorUtils.locstring.json';
import loc from 'owa-localize';
import { UnifiedGroupFilter, UnifiedGroupSorting } from '../../models/unifiedGroupListViewState';
import type { AppState } from '../../../appstate';
import { getGuid } from 'owa-guid';
import type {
    UnifiedGroupListEntry,
    UnifiedGroupEntry,
    GroupedUnifiedGroupEntries,
} from '../../selectors/groups/unifiedGroupListSelector';
import { initial } from '../../utils/groups/initial';
import {
    compareByGivenNameAndSurname,
    compareByRelevancy,
} from '../../utils/groups/unifiedGroupSorting';

import {
    joinedUnifiedGroupsLeftNavId,
    ownedUnifiedGroupsLeftNavId,
    HubListEntryKind,
    UnifiedGroup,
    deletedUnifiedGroupsLeftNavId,
} from '../../../shared';
import DisplayOrder from 'owa-people-types/lib/types/DisplayOrder';

// Threshold for alphabetical lists to not display the group

export const NO_INITIAL_INDICATOR = '🞼';

const NUM_DAYS_FOR_WEEK = 7;

/**
 * Applies filtering and sorting to a list of entries based on a viewState
 */
export function filterAndSortUnifiedGroups(
    groups: UnifiedGroup[],
    filter: UnifiedGroupFilter,
    sorting: UnifiedGroupSorting,
    displayOrder: DisplayOrder,
    activeLeftNavItemId: string,
    culture: string
): UnifiedGroup[] {
    // The viewState contains the current filter, sorting criteria and display order.

    const filteredGroups = filterUnifiedGroups(
        groups,
        activeLeftNavItemId === ownedUnifiedGroupsLeftNavId ? filter : UnifiedGroupFilter.All
    );

    return filteredGroups
        ? filteredGroups.sort(
              getCompareUnifiedGroupsFunction(sorting, culture, displayOrder, activeLeftNavItemId)
          )
        : [];
}

/**
 * Uses the group function to insert group headers where appropriate for the entries array.
 * The provided list is already sorted, alphabetical sorted lists with NO_GROUP_THRESHOLD or less items are not grouped.
 */
export function applyGrouping(
    activeLeftNavItemId: string,
    entries: UnifiedGroup[],
    sorting: UnifiedGroupSorting
): UnifiedGroupListEntry[] {
    type GroupToUnifiedGroupsMapping = { [group: string]: UnifiedGroupEntry[] };

    if (
        activeLeftNavItemId === deletedUnifiedGroupsLeftNavId &&
        sorting === UnifiedGroupSorting.ByRelevancy
    ) {
        return entries.map(toUnifiedGroupListElement);
    }

    // empty entries are marked with a guid and replaced with a label after grouping
    const emptyMarker = getGuid();
    const group = unifiedGroupingFunction(sorting, emptyMarker, activeLeftNavItemId);

    const groupedEntries: GroupToUnifiedGroupsMapping = entries.reduce<GroupToUnifiedGroupsMapping>(
        (
            groupedEntries: GroupToUnifiedGroupsMapping,
            current: UnifiedGroup
        ): GroupToUnifiedGroupsMapping => {
            if (current) {
                const currentGroup = group(current);

                if (!groupedEntries.hasOwnProperty(currentGroup)) {
                    groupedEntries[currentGroup] = [toUnifiedGroupListElement(current)];
                } else {
                    groupedEntries[currentGroup].push(toUnifiedGroupListElement(current));
                }
            }

            return groupedEntries;
        },
        {}
    );

    const sectionsAlwaysVisibleInApplicableTab = [loc(actionableGroups), loc(groupsJoinedRecently)];

    const sectionKeys = Object.keys(groupedEntries);

    // No need to show sections if there is only one and it is not part of the exception list
    if (
        sectionKeys.length === 1 &&
        !sectionsAlwaysVisibleInApplicableTab.includes(sectionKeys[0])
    ) {
        return entries.map(toUnifiedGroupListElement);
    }

    const sortedKeys = sectionKeys
        .filter(groupHeader => groupHeader !== emptyMarker)
        .sort(function (left: string, right: string): number {
            return getHeaderRank(left) - getHeaderRank(right);
        })
        .concat(groupedEntries[emptyMarker] ? [emptyMarker] : []);

    return sortedKeys.reduce<UnifiedGroupListEntry[]>(
        (grouped: UnifiedGroupListEntry[], group: string): UnifiedGroupListEntry[] => {
            return grouped
                .concat(toGroupIndex(group, sorting, emptyMarker))
                .concat(groupedEntries[group]);
        },
        []
    );
}

export function makeGroupedEntries(
    groups: UnifiedGroup[],
    filter: UnifiedGroupFilter,
    sorting: UnifiedGroupSorting,
    displayOrder: DisplayOrder,
    activeLeftNavItemId: string,
    culture: string
): UnifiedGroupListEntry[] {
    const groupedEntries =
        groups.length > 0
            ? filterAndSortUnifiedGroups(
                  groups,
                  filter,
                  sorting,
                  displayOrder,
                  activeLeftNavItemId,
                  culture
              ).map(toUnifiedGroupListElement)
            : [];

    return applyGrouping(activeLeftNavItemId, groupedEntries, sorting);
}

export function toUnifiedGroupListElement(unifiedGroup: UnifiedGroup): UnifiedGroupEntry {
    return {
        kind: HubListEntryKind.Content,
        ...unifiedGroup,
    };
}

function getCompareUnifiedGroupsFunction(
    sorting: UnifiedGroupSorting,
    locale: string,
    displayOrder: DisplayOrder,
    activeLeftNavItemId: string
): (left: UnifiedGroup, right: UnifiedGroup) => number {
    switch (sorting) {
        case UnifiedGroupSorting.ByRelevancy:
            return compareByRelevancy(locale, displayOrder, activeLeftNavItemId);
        case UnifiedGroupSorting.ByFirstName:
        default:
            return compareByGivenNameAndSurname(locale, displayOrder);
    }
}

export function toGroupIndex(
    groupLabel: string,
    sorting: UnifiedGroupSorting,
    emptyMarker: string
): GroupedUnifiedGroupEntries {
    if (groupLabel !== emptyMarker) {
        return {
            kind: HubListEntryKind.Group,
            index: groupLabel,
        };
    }
    switch (sorting) {
        case UnifiedGroupSorting.ByRelevancy:
        case UnifiedGroupSorting.ByFirstName:
        default:
            return {
                kind: HubListEntryKind.Group,
                index: NO_INITIAL_INDICATOR,
            };
    }
}

function filterUnifiedGroups(groups: UnifiedGroup[], filter: UnifiedGroupFilter): UnifiedGroup[] {
    switch (filter) {
        case UnifiedGroupFilter.MembershipRequests:
            return groups.filter(group => group.pendingMembershipRequests > 0);
        case UnifiedGroupFilter.ExpiringGroups:
            return groups.filter(group => group.isExpiring);
        default:
            return groups;
    }
}

function unifiedGroupingFunction(
    sorting: UnifiedGroupSorting,
    emptyMarker: string,
    activeLeftNavItemId: string
): (entity: UnifiedGroup) => string {
    switch (sorting) {
        case UnifiedGroupSorting.ByRelevancy:
            return (entity: UnifiedGroup) => {
                if (activeLeftNavItemId === ownedUnifiedGroupsLeftNavId) {
                    return ownedGroupsSectionHeader(entity);
                }

                return joinedGroupsSectionHeader(entity);
            };
        case UnifiedGroupSorting.ByFirstName:
        default:
            return (entity: UnifiedGroup) => initial(entity, sorting, emptyMarker);
    }
}

function ownedGroupsSectionHeader(entity: UnifiedGroup): string {
    if (entity.pendingMembershipRequests > 0 || entity.isExpiring) {
        return loc(actionableGroups);
    }

    return loc(otherGroupsUserOwns);
}

function joinedGroupsSectionHeader(entity: UnifiedGroup): string {
    if (hasJoinedThisWeek(entity.joinedDateTime)) {
        return loc(groupsJoinedRecently);
    }

    return loc(allOtherGroups);
}

function hasJoinedThisWeek(joinedDate: Date): boolean {
    const dateAWeekAgo = new Date();
    dateAWeekAgo.setDate(dateAWeekAgo.getDate() - NUM_DAYS_FOR_WEEK);

    return joinedDate.getTime() > dateAWeekAgo.getTime();
}

function getHeaderRank(sectionName: string): number {
    if (sectionName === loc(groupsJoinedRecently)) {
        return 0;
    }

    if (sectionName === loc(actionableGroups)) {
        return 1;
    }

    return sectionName.charCodeAt(0);
}

export function getInitialGroupsToPrefetch(appState: AppState): UnifiedGroup[] {
    const unifiedGroups = appState.data.groupsHubState.unifiedGroupsMap;
    const joinedGroup = appState.data.groupsHubState.joinedGroupsIds.map(key => unifiedGroups[key]);
    const ownedGroup = appState.data.groupsHubState.ownedGroupsIds.map(key => unifiedGroups[key]);
    const userCulture = appState.data.userConfiguration.settings.UserCulture;
    let initialGroups: UnifiedGroup[] = [];
    if (joinedGroup.length > 0) {
        initialGroups.push(
            getFirstGroupInList(joinedGroup, joinedUnifiedGroupsLeftNavId, userCulture)
        );
    }
    if (ownedGroup.length > 0) {
        initialGroups.push(
            getFirstGroupInList(ownedGroup, ownedUnifiedGroupsLeftNavId, userCulture)
        );
    }
    return initialGroups;
}

function getFirstGroupInList(
    entries: UnifiedGroup[],
    listLabel: string,
    userCulture: string
): UnifiedGroup {
    let listEntries = makeGroupedEntries(
        entries,
        UnifiedGroupFilter.All,
        UnifiedGroupSorting.ByRelevancy,
        DisplayOrder.FirstNameLastName,
        listLabel,
        userCulture
    );
    const groupListElements = listEntries.filter(
        entry => entry.kind === HubListEntryKind.Content
    ) as UnifiedGroup[];
    return groupListElements[0];
}
